/**
 * Shared data surfaces for dark demo workbenches.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../../styling/composeClassNames.js";
import { DemoDataTable, type DemoDataTableColumn } from "./surfaces.js";
import { DemoMetricCard } from "./surfaces.js";
import { getDemoWorkbenchToneColor, type DemoWorkbenchTone } from "./workbench-colors.js";

const VALUE_COLORS: Readonly<Record<string, string>> = {
  "text-amber-400": "#fbbf24",
  "text-emerald-400": "#34d399",
  "text-teal-400": "#2dd4bf",
  "text-violet-400": "#a78bfa",
};

function resolveValueColor(color: string): string {
  if (color.startsWith("#") || color.startsWith("rgb")) return color;
  return VALUE_COLORS[color] ?? "#2dd4bf";
}

export type DemoWorkbenchCopyTone = "body" | "bright" | "danger" | "muted";

export interface DemoWorkbenchCopyProps {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly size?: "xs" | "sm";
  readonly style?: React.CSSProperties;
  readonly tone?: DemoWorkbenchCopyTone;
}

const COPY_TONE_COLOR: Readonly<Record<DemoWorkbenchCopyTone, string>> = {
  body: "#cbd5e1",
  bright: "#f8fafc",
  danger: "#fca5a5",
  muted: "#94a3b8",
};

export function DemoWorkbenchCopy({
  children,
  className,
  size = "sm",
  style,
  tone = "body",
}: DemoWorkbenchCopyProps): React.JSX.Element {
  return (
    <p
      className={cn("m-0", className)}
      style={{
        color: COPY_TONE_COLOR[tone],
        fontSize: size === "xs" ? 12 : 14,
        lineHeight: 1.55,
        ...style,
      }}
    >
      {children}
    </p>
  );
}

export interface DemoWorkbenchFieldProps {
  readonly label: React.ReactNode;
  readonly value?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly mono?: boolean;
}

export function DemoWorkbenchField({
  label,
  value,
  children,
  mono = true,
}: DemoWorkbenchFieldProps): React.JSX.Element {
  return (
    <div>
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: "#e2e8f0",
          fontFamily: mono ? "ui-monospace, monospace" : "inherit",
          fontSize: 14,
          fontWeight: 700,
          marginTop: 2,
        }}
      >
        {children ?? value}
      </div>
    </div>
  );
}

export type DemoWorkbenchFactTone = DemoWorkbenchTone;

export interface DemoWorkbenchFactGridProps {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly minColumnWidth?: number;
  readonly style?: React.CSSProperties;
}

export function DemoWorkbenchFactGrid({
  children,
  className,
  minColumnWidth = 96,
  style,
}: DemoWorkbenchFactGridProps): React.JSX.Element {
  return (
    <div
      className={className}
      style={{
        display: "grid",
        gap: 6,
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export interface DemoWorkbenchFactProps {
  readonly label: React.ReactNode;
  readonly value: React.ReactNode;
  readonly detail?: React.ReactNode;
  readonly mono?: boolean;
  readonly tone?: DemoWorkbenchFactTone;
}

export function DemoWorkbenchFact({
  label,
  value,
  detail,
  mono = false,
  tone = "default",
}: DemoWorkbenchFactProps): React.JSX.Element {
  const color = getDemoWorkbenchToneColor(tone);
  return (
    <div
      style={{
        background: "rgba(15, 23, 42, 0.54)",
        border: `1px solid ${color.border}`,
        borderRadius: 8,
        padding: "7px 8px",
      }}
    >
      <div style={{ color: "#94a3b8", fontSize: 11 }}>{label}</div>
      <div
        style={{
          color: color.fg,
          fontFamily: mono ? "ui-monospace, monospace" : "inherit",
          fontSize: 12,
          fontWeight: 800,
          marginTop: 2,
        }}
      >
        {value}
      </div>
      {detail !== undefined ? (
        <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>{detail}</div>
      ) : null}
    </div>
  );
}

export interface DemoWorkbenchListItem {
  readonly id: React.Key;
  readonly label: React.ReactNode;
  readonly detail?: React.ReactNode;
  readonly tone?: DemoWorkbenchFactTone;
  readonly mono?: boolean;
}

export interface DemoWorkbenchItemListProps {
  readonly items: readonly DemoWorkbenchListItem[];
  readonly className?: string;
  readonly compact?: boolean;
  readonly emptyState?: React.ReactNode;
  readonly maxItems?: number;
  readonly overflowLabel?: (hiddenCount: number) => React.ReactNode;
}

export function DemoWorkbenchItemList({
  items,
  className,
  compact = false,
  emptyState = "No entries",
  maxItems,
  overflowLabel = (hiddenCount): React.ReactNode => `+${String(hiddenCount)} more`,
}: DemoWorkbenchItemListProps): React.JSX.Element {
  const visibleItems = maxItems === undefined ? items : items.slice(0, maxItems);
  const hiddenCount =
    maxItems === undefined ? 0 : Math.max(0, items.length - maxItems);
  if (items.length === 0) {
    return (
      <div className={cn("text-xs text-slate-500", className)}>{emptyState}</div>
    );
  }

  return (
    <ul className={cn("m-0 list-none space-y-1 p-0", className)}>
      {visibleItems.map((item) => {
        const color = getDemoWorkbenchToneColor(item.tone ?? "default");
        return (
          <li
            key={item.id}
            style={{
              alignItems: compact ? "baseline" : "center",
              color: color.fg,
              display: "flex",
              fontFamily: item.mono ? "ui-monospace, monospace" : "inherit",
              fontSize: compact ? 11 : 12,
              gap: 6,
              lineHeight: 1.4,
            }}
          >
            <span className="min-w-0 truncate">{item.label}</span>
            {item.detail !== undefined ? (
              <span className="shrink-0 text-slate-600">{item.detail}</span>
            ) : null}
          </li>
        );
      })}
      {hiddenCount > 0 ? (
        <li className="text-[10px] text-slate-600">
          {overflowLabel(hiddenCount)}
        </li>
      ) : null}
    </ul>
  );
}

export interface DemoWorkbenchMetricCardProps {
  readonly label: string;
  readonly value: React.ReactNode;
  readonly sub?: React.ReactNode;
  readonly color?: string;
}

export function DemoWorkbenchMetricCard({
  label,
  value,
  sub,
  color = "text-teal-400",
}: DemoWorkbenchMetricCardProps): React.JSX.Element {
  return (
    <DemoMetricCard
      label={label}
      value={value}
      sub={sub}
      color={resolveValueColor(color)}
      tone="inherit"
      className="hub-metric-card"
    />
  );
}

export interface DemoWorkbenchTableProps {
  readonly headers: readonly string[];
  readonly rows: readonly (readonly React.ReactNode[])[];
  readonly compact?: boolean;
}

export function DemoWorkbenchTable({
  headers,
  rows,
  compact,
}: DemoWorkbenchTableProps): React.JSX.Element {
  const columns = headers.map<DemoDataTableColumn<readonly React.ReactNode[]>>((header, index) => ({
    id: `${index}:${header}`,
    header,
    cell: (row): React.ReactNode => row[index] ?? null,
  }));

  return (
    <DemoDataTable
      ariaLabel="Workbench data"
      columns={columns}
      rows={rows}
      getRowKey={(_row, rowIndex) => rowIndex}
      tone="dark"
      className="hub-data-table"
      emptyState="No entries"
      {...(compact === undefined ? {} : { compact })}
    />
  );
}
